import * as React from 'react';

import { getCatalogCategories } from 'components/catalogs/libs/catalogApiClient';

type CatalogSummary = import('components/catalogs/libs/catalogApiClient').CatalogSummary;

export type CatalogCategory = {
  code: string;
  name: string;
  imageUrl: string;
};

type Args = {
  catalogCode: string;
  onError: (error: unknown) => void;
};

type Result = {
  catalog: CatalogSummary | null;
  categories: CatalogCategory[] | null;
  hasError: boolean;
};

const parseCatalog = (data: unknown): CatalogSummary | null => {
  const catalog = (data as { catalog?: unknown }).catalog;
  if (!catalog || typeof catalog !== 'object') {
    return null;
  }

  const name = (catalog as { name?: unknown }).name;
  const code = (catalog as { code?: unknown }).code;
  const topImageUrl = (catalog as { topImageUrl?: unknown }).topImageUrl;

  if (typeof name !== 'string' || typeof code !== 'string' || typeof topImageUrl !== 'string') {
    return null;
  }

  return { name, code, topImageUrl };
};

const parseCategories = (data: unknown): CatalogCategory[] => {
  const categories = (data as { categories?: unknown }).categories;
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories
    .map((raw) => {
      const code = (raw as { code?: unknown }).code;
      const name = (raw as { name?: unknown }).name;
      const imageUrl = (raw as { imageUrl?: unknown }).imageUrl;
      if (typeof code !== 'string' || typeof name !== 'string') {
        return null;
      }
      return { code, name, imageUrl: typeof imageUrl === 'string' ? imageUrl : '' };
    })
    .filter((v): v is CatalogCategory => v !== null);
};

export const useCatalogCategories = ({ catalogCode, onError }: Args): Result => {
  const [catalog, setCatalog] = React.useState<CatalogSummary | null>(null);
  const [categories, setCategories] = React.useState<CatalogCategory[] | null>(null);
  const [hasError, setHasError] = React.useState(false);

  const isActiveRef = React.useRef(true);
  React.useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    let isCancelled = false;

    const load = async (): Promise<void> => {
      if (!catalogCode) {
        if (isCancelled || !isActiveRef.current) {
          return;
        }
        setCatalog(null);
        setCategories([]);
        setHasError(false);
        return;
      }

      try {
        if (isCancelled || !isActiveRef.current) {
          return;
        }

        setHasError(false);
        const response = await getCatalogCategories(catalogCode);
        if (isCancelled || !isActiveRef.current) {
          return;
        }

        setCatalog(parseCatalog(response));
        setCategories(parseCategories(response));
      } catch (err) {
        if (isCancelled || !isActiveRef.current) {
          return;
        }
        setHasError(true);
        onError(err);
        setCatalog(null);
        setCategories([]);
      }
    };

    void load();
    return () => {
      isCancelled = true;
    };
  }, [catalogCode, onError]);

  return { catalog, categories, hasError };
};
